import { type ComponentProps, useLayoutEffect, useRef } from 'react'
import { twMerge } from 'tailwind-merge'

function Textarea({ className, children, ...props }: ComponentProps<'div'>) {
  return (
    <div data-slot="textarea" className={twMerge('flex flex-col gap-2', className)} {...props}>
      {children}
    </div>
  )
}

function TextareaLabel({ className, children, ...props }: ComponentProps<'h3'>) {
  return (
    <h3
      data-slot="textarea-label"
      className={twMerge('text-foreground-secondary text-sm font-medium', className)}
      {...props}
    >
      {children}
    </h3>
  )
}

interface TextareaFieldProps extends ComponentProps<'textarea'> {
  /** Grow the field to fit its content instead of scrolling internally. */
  autoResize?: boolean
}

function TextareaField({ className, autoResize, ...props }: TextareaFieldProps) {
  const ref = useRef<HTMLTextAreaElement>(null)

  useLayoutEffect(() => {
    if (!autoResize) return
    const el = ref.current
    if (!el) return
    // Collapsing to 'auto' momentarily shrinks the field to its min-height,
    // which clamps the scroll position of any scrollable ancestor and makes
    // the viewport jump on every keystroke when editing long content.
    // Snapshot ancestor scroll positions and restore them after measuring.
    const scrollParents: Array<[HTMLElement, number]> = []
    for (let parent = el.parentElement; parent; parent = parent.parentElement) {
      if (parent.scrollHeight > parent.clientHeight) scrollParents.push([parent, parent.scrollTop])
    }
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
    for (const [parent, scrollTop] of scrollParents) parent.scrollTop = scrollTop
  }, [autoResize, props.value])

  return (
    <textarea
      ref={autoResize ? ref : undefined}
      data-slot="textarea-field"
      className={twMerge(
        'text-md flex w-full min-w-0 resize-y px-4 py-3 font-medium', // Layout
        'rounded-lg border bg-transparent outline-none transition-[color,box-shadow]', // Container
        'placeholder:text-foreground-disabled text-foreground-secondary', // Text
        'border-border hover:border-hover focus-visible:border-[color:var(--focus-ring-color)]', // Border and focus state
        'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50', // Disabled state
        'aria-invalid:ring-danger/20 dark:aria-invalid:ring-danger/40 aria-invalid:border-danger', // Data validation
        // Auto-resize grows the field to its content — drop the manual
        // handle and the inner scrollbar so the page scrolls instead.
        autoResize && 'resize-none overflow-hidden',
        className,
      )}
      {...props}
    />
  )
}

Textarea.Label = TextareaLabel
Textarea.Field = TextareaField

export { Textarea }
